<script setup lang="ts">
import {computed} from "vue";
import {useI18n} from "vue-i18n";
import {shouldConsiderComponentAsOk} from "@/domains/scheduling/domains/components/utils";
import {
  SchedulingComponentStatus,
  SchedulingComponentStatusOperation,
} from "@/domains/scheduling/domains/components/types";

interface SchedulingComponentStatusChipProps {
  component: {
    available_stock: number;
    next_shortage_date?: string;
    required_amount?: number;
  };
  operation?: SchedulingComponentStatusOperation;
}

const props = defineProps<SchedulingComponentStatusChipProps>();

const {t} = useI18n();

const status = computed<SchedulingComponentStatus>(() => {
  if (shouldConsiderComponentAsOk(props.operation))
    return SchedulingComponentStatus.Ok;

  if (props.component.available_stock <= (props.component.required_amount || 0))
    return SchedulingComponentStatus.Shortage;
  if (props.component.next_shortage_date)
    return SchedulingComponentStatus.UpcomingShortage;
  return SchedulingComponentStatus.Ok;
});
const statusClasses = computed<string>(() => {
  if (status.value === SchedulingComponentStatus.Shortage)
    return "text-error-primary bg-error-primary";
  if (status.value === SchedulingComponentStatus.UpcomingShortage)
    return "text-warning-primary bg-warning-primary";
  return "text-success-primary bg-success-primary";
});
const statusIconName = computed<string>(() => {
  if (
    [
      SchedulingComponentStatus.Shortage,
      SchedulingComponentStatus.UpcomingShortage,
    ].includes(status.value)
  )
    return "alert-triangle";
  return "check";
});
</script>

<template>
  <span :class="statusClasses" class="scheduling-components-status-chip">
    <OpalIcon :name="statusIconName" />

    {{ t(`SchedulingComponentsStatusChip.${status}`) }}
  </span>
</template>

<style scoped lang="scss">
.scheduling-components-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-radius: 4px;
}
</style>
